// The controls every long list shares (#49, #50): how many rows to show (50, 100 or all), the pages,
// and headings that sort. The list itself is filtered, sorted and counted on the server.
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@elysian/ui';

export const PAGE_SIZES = [{ key: 50, label: '50' }, { key: 100, label: '100' }, { key: 0, label: 'All' }] as const;

/** A value kept between visits (local) or for the session (to come back to the same place), quietly if storage isn't there. */
export function useRemembered<T>(key: string, initial: T, where: 'local' | 'session' = 'local'): [T, (v: T | ((p: T) => T)) => void] {
  const store = () => (where === 'local' ? localStorage : sessionStorage);
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = store().getItem(key);
      return raw === null ? initial : (JSON.parse(raw) as T);
    } catch { return initial; }
  });
  useEffect(() => {
    try { store().setItem(key, JSON.stringify(value)); } catch { /* not remembered: fine */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, value]);
  return [value, setValue];
}

/** A search box's text, settled a moment after typing stops, so the server isn't asked on every key. */
export function useSettled(value: string, ms = 300): string {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return settled;
}

export function ShowSize({ value, onChange }: { value: number; onChange: (n: number) => void }) {
  return (
    <label className="el-row" style={{ gap: 6, alignItems: 'center', fontSize: 14 }}>
      Show
      <select className="el-input" aria-label="Rows to show" style={{ width: 'auto' }} value={value} onChange={(e) => onChange(Number(e.target.value))}>
        {PAGE_SIZES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
      </select>
    </label>
  );
}

/** "Showing 51–100 of 1,240", with Previous and Next when there's more than one page. "All" stops at 5,000, and says so. */
export function Pager({ count, page, size, shown, onPage }: { count: number; page: number; size: number; shown: number; onPage: (p: number) => void }) {
  if (count === 0) return null;
  const first = page * (size || 0) + 1;
  const last = first + shown - 1;
  const pages = size ? Math.max(1, Math.ceil(count / size)) : 1;
  return (
    <div className="el-spread" style={{ padding: '10px 16px 14px', alignItems: 'center' }}>
      <span className="el-hint" role="status">
        Showing {first.toLocaleString('en-IE')}–{last.toLocaleString('en-IE')} of {count.toLocaleString('en-IE')}
        {!size && count > shown ? ': the first 5,000. Narrow it with the dates or search' : ''}
      </span>
      {size > 0 && pages > 1 && (
        <div className="el-row" style={{ gap: 8 }}>
          <Button size="sm" disabled={page === 0} onClick={() => onPage(page - 1)}>Previous</Button>
          <Button size="sm" disabled={page + 1 >= pages} onClick={() => onPage(page + 1)}>Next</Button>
        </div>
      )}
    </div>
  );
}

export type SortState<K extends string> = { sort: K; dir: 'asc' | 'desc' };

/**
 * A column heading that sorts (#50): click to sort by it, again for the other way. Dates and amounts start
 * with the newest or largest; names and numbers from the start of the alphabet.
 */
export function SortTh<K extends string>({ label, col, state, onSort, numeric, firstDir }: {
  label: string; col: K; state: SortState<K>; onSort: (s: SortState<K>) => void; numeric?: boolean; firstDir?: 'asc' | 'desc';
}) {
  const on = state.sort === col;
  const next: SortState<K> = on ? { sort: col, dir: state.dir === 'asc' ? 'desc' : 'asc' } : { sort: col, dir: firstDir ?? (numeric ? 'desc' : 'asc') };
  return (
    <th scope="col" className={numeric ? 'el-num' : undefined} aria-sort={on ? (state.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" className="el-sort" data-on={on || undefined} onClick={() => onSort(next)}
        title={`Sort by ${label.toLowerCase()}${on ? `, ${next.dir === 'asc' ? 'A to Z or smallest first' : 'Z to A or largest first'}` : ''}`}>
        {label}<span aria-hidden="true" className="el-sort-mark">{on ? (state.dir === 'asc' ? '▲' : '▼') : ''}</span>
      </button>
    </th>
  );
}

/**
 * For a list already loaded in full (journals, contacts, products, bank lines): the same 50 / 100 / All, pages
 * and sorting, over every row. `filterKey` changes (a search, a tab) send it back to the first page.
 */
export function useClientList<T, K extends string>(rows: T[], opts: {
  key: string; initial: SortState<NoInfer<K>>; sorts: Record<K, (r: T) => string | number | null | undefined>; filterKey?: string;
}) {
  const [size, setSize] = useRemembered(`${opts.key}.size`, 50);
  const [sortBy, setSortBy] = useRemembered<SortState<K>>(`${opts.key}.sort`, opts.initial);
  const [page, setPage] = useRemembered(`${opts.key}.page`, 0, 'session');
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    setPage(0);
  }, [opts.filterKey, sortBy.sort, sortBy.dir, size, setPage]);
  const sortFn = opts.sorts[sortBy.sort] ?? Object.values(opts.sorts)[0] as (r: T) => string | number | null | undefined;
  const sorted = useMemo(() => rows.map((r, i) => ({ r, i, k: sortFn(r) })).sort((a, b) => {
    if (a.k === b.k) return a.i - b.i;            // ties keep the list's own order
    if (a.k === null || a.k === undefined || a.k === '') return 1;
    if (b.k === null || b.k === undefined || b.k === '') return -1;
    const c = typeof a.k === 'number' && typeof b.k === 'number' ? a.k - b.k : String(a.k).localeCompare(String(b.k), 'en-IE', { numeric: true, sensitivity: 'base' });
    return sortBy.dir === 'asc' ? c : -c;
  }).map((x) => x.r), [rows, sortFn, sortBy.dir]);
  const capped = sorted.slice(0, 5000);
  const pages = size ? Math.max(1, Math.ceil(capped.length / size)) : 1;
  const at = Math.min(page, pages - 1);
  const shown = size ? capped.slice(at * size, at * size + size) : capped;
  return { rows: shown, all: sorted, count: rows.length, size, setSize, page: at, setPage, sortBy, setSortBy };
}
